import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService, User, ApiKey, FeedStatsItem, FeedHistoryItem, SystemHealth } from '../services/apiService';
import BrandLogo from './BrandLogo';

// ---------------------------------------------------------------------------
// Presentation lives in App.css. This file carries structure and data only —
// the class names below are the shared vocabulary (admin-card, admin-table,
// admin-btn, status-badge …) rather than one-off inline styles.
// ---------------------------------------------------------------------------

// Inline error state with a retry action, rendered in place of empty/zero data
const ErrorBanner: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div data-testid="error-banner" role="alert" className="admin-error">
    <span>{message}</span>
    <button type="button" onClick={onRetry}>Retry</button>
  </div>
);

// Second user of this toolbar (Acquisition was the first), which is the point
// at which the markup becomes one thing rather than two copies of the
// admin-toolbar / admin-segment / aria structure.
const WindowPicker: React.FC<{ days: number; options: number[]; onChange: (d: number) => void }> = ({
  days, options, onChange
}) => (
  <div className="admin-toolbar">
    <span className="admin-muted">Window:</span>
    <div className="admin-segment" role="group" aria-label="Reporting window">
      {options.map(d => (
        <button key={d} type="button" onClick={() => onChange(d)} aria-pressed={days === d}>
          {d}d
        </button>
      ))}
    </div>
  </div>
);

const EmptyState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="admin-empty"><p>{children}</p></div>
);

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function statusBadgeClass(isActive: boolean): string {
  return `status-badge ${isActive ? 'is-active' : 'is-inactive'}`;
}

function statusLabel(status: string): string {
  switch (status) {
    case 'active': return 'Active';
    case 'suspended': return 'Suspended';
    case 'pending': return 'Pending';
    default: return 'Inactive';
  }
}

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  // Mobile only: the sidebar collapses to a topbar and the sections live
  // behind a hamburger. Desktop CSS shows the nav regardless of this flag.
  const [navOpen, setNavOpen] = useState(false);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'api-keys', label: 'API Keys', icon: '🔑' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'feeds', label: 'Feeds', icon: '📡' },
    { id: 'matching', label: 'Matching', icon: '🎯' },
    { id: 'acquisition', label: 'Acquisition', icon: '📈' },
    { id: 'audit-funnel', label: 'Audit funnel', icon: '🔻' },
    { id: 'prospects', label: 'Prospects', icon: '🔍' },
    { id: 'outreach', label: 'Outreach', icon: '📮' },
    { id: 'flags', label: 'Flags', icon: '🚩' },
    { id: 'health', label: 'Health', icon: '🏥' }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'api-keys':
        return <ApiKeysContent />;
      case 'users':
        return <UsersContent />;
      case 'feeds':
        return <FeedsContent />;
      case 'matching':
        return <MatchingContent />;
      case 'acquisition':
        return <AcquisitionContent />;
      case 'audit-funnel':
        return <AuditFunnelContent />;
      case 'prospects':
        return <ProspectsContent />;
      case 'outreach':
        return <OutreachContent />;
      case 'flags':
        return <FlagsContent />;
      case 'health':
        return <HealthContent />;
      case 'dashboard':
      default:
        return <DashboardContent />;
    }
  };

  return (
    <div data-testid="admin-dashboard" className="admin-shell">
      {/* Sidebar — a topbar with a hamburger below 900px */}
      <aside data-testid="admin-sidebar" className="admin-sidebar">
        <div className="admin-topbar">
          <div className="admin-identity">
            <div className="admin-wordmark">
              <BrandLogo />
              <span className="admin-label">Admin</span>
            </div>
            <p className="admin-identity-email">{user?.email}</p>
          </div>

          <button
            type="button"
            className="admin-nav-toggle"
            aria-expanded={navOpen}
            aria-controls="admin-nav"
            onClick={() => setNavOpen(open => !open)}
          >
            <span className="visually-hidden">{navOpen ? 'Close menu' : 'Open menu'}</span>
          </button>
        </div>

        <nav
          id="admin-nav"
          data-testid="admin-nav"
          className="admin-nav"
          data-open={navOpen ? 'true' : 'false'}
          aria-label="Admin sections"
        >
          {navItems.map(item => (
            <button
              key={item.id}
              type="button"
              data-testid={`nav-${item.id}`}
              onClick={() => { setActiveTab(item.id); setNavOpen(false); }}
              className={`admin-nav-item${activeTab === item.id ? ' active' : ''}`}
              aria-current={activeTab === item.id ? 'page' : undefined}
            >
              <span className="admin-nav-icon" aria-hidden="true">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        <div className="admin-session-actions" data-open={navOpen ? 'true' : 'false'}>
          <button
            type="button"
            data-testid="logout-button"
            className="admin-btn-secondary admin-signout"
            onClick={logout}
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="content-area">
        <div className="admin-content-card">
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

// Dashboard Content Component
// "user_registered" -> "User registered" (covers every activity type)
function activityTypeLabel(type: string): string {
  const label = type.replace(/_/g, ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

const DashboardContent: React.FC = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalApiKeys: 0,
    activeUsers: 0,
    systemHealth: 'unknown' as string
  });
  const [statsError, setStatsError] = useState<string | null>(null);
  const [activities, setActivities] = useState<Array<{ id: string; type: string; message: string; timestamp: string }>>([]);
  const [activityError, setActivityError] = useState<string | null>(null);

  const fetchStats = async () => {
    setStatsError(null);
    try {
      const data = await apiService.getDashboardStats();
      setStats({
        totalUsers: data.totalUsers,
        totalApiKeys: data.totalApiKeys,
        activeUsers: data.activeUsers,
        systemHealth: data.systemHealth
      });
    } catch (error) {
      setStatsError(errorMessage(error, 'Failed to load dashboard stats.'));
    }
  };

  const fetchActivities = async () => {
    setActivityError(null);
    try {
      const data = await apiService.getRecentActivity({ limit: 10 });
      setActivities(data.activities || []);
    } catch (error) {
      setActivityError(errorMessage(error, 'Failed to load recent activity.'));
    }
  };

  useEffect(() => {
    fetchStats();
    fetchActivities();
  }, []);

  return (
    <div>
      <h1 className="admin-page-title">Admin Dashboard</h1>
      <p className="admin-page-lead">Accounts, keys and service health at a glance.</p>

      {statsError ? (
        <ErrorBanner message={statsError} onRetry={fetchStats} />
      ) : (
        <div className="stats-grid">
          <StatCard title="Total Users" value={stats.totalUsers} />
          <StatCard title="API Keys" value={stats.totalApiKeys} />
          <StatCard title="Active Users" value={stats.activeUsers} />
          <StatCard title="System Health" value={stats.systemHealth} />
        </div>
      )}

      <div data-testid="recent-activity">
        <h3 className="admin-section-title">Recent Activity</h3>
        {activityError ? (
          <ErrorBanner message={activityError} onRetry={fetchActivities} />
        ) : (
          <div className="activity-list">
            {activities.length === 0 ? (
              <div className="activity-row"><span className="admin-muted">No recent activity.</span></div>
            ) : (
              activities.map(a => (
                <div key={a.id} className="activity-row">
                  <span className="activity-type">{activityTypeLabel(a.type)}</span>
                  <span className="activity-message">{a.message}</span>
                  <span className="activity-time">{new Date(a.timestamp).toLocaleString()}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// API Keys Content Component
const ApiKeysContent: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editKey, setEditKey] = useState<ApiKey | null>(null);
  const [formDomain, setFormDomain] = useState('');
  // A STRING, not a number, because the meaningful default is "no override" and
  // a number type cannot represent absence. It used to be `useState(1000)`,
  // which pre-filled the create form with an explicit 1000/hour — BELOW every
  // plan on the ladder (free 3,000 · starter 12,000 · publisher 48,000), so the
  // fastest path through the dialog silently throttled the account it was
  // provisioning. Migration 028 nulled exactly these rows; this form put them
  // straight back.
  const [formRateLimit, setFormRateLimit] = useState('');
  const [formStatus, setFormStatus] = useState('active');
  const [planRateLimits, setPlanRateLimits] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    setError(null);
    try {
      const data = await apiService.getApiKeys();
      setApiKeys(data.apiKeys || []);
      setPlanRateLimits((data as any).planRateLimits || {});
    } catch (error) {
      setError(errorMessage(error, 'Failed to load API keys.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (key: ApiKey) => {
    if (!window.confirm(`Delete the API key for ${key.domain}? This cannot be undone.`)) return;
    try {
      await apiService.deleteApiKey(key.id);
      setApiKeys(prev => prev.filter(k => k.id !== key.id));
    } catch (error) {
      setError(errorMessage(error, 'Failed to delete API key.'));
    }
  };

  const handleCreate = async () => {
    setError(null);
    try {
      // Omitted entirely when blank: the route stores NULL, and the key then
      // derives its ceiling from the owner's plan. Sending a number here is a
      // deliberate, permanent override — never a default.
      const override = formRateLimit.trim();
      await apiService.createApiKey({
        domain: formDomain,
        ...(override ? { rateLimit: Number(override) } : {}),
      });
      setShowCreate(false);
      setFormDomain('');
      setFormRateLimit('');
      fetchApiKeys();
    } catch (error) {
      setError(errorMessage(error, 'Failed to create API key.'));
    }
  };

  const handleEdit = async () => {
    if (!editKey) return;
    setError(null);
    try {
      const override = formRateLimit.trim();
      await apiService.updateApiKey(editKey.id, {
        status: formStatus as any,
        ...(override ? { rateLimit: Number(override) } : {}),
      });
      setEditKey(null);
      fetchApiKeys();
    } catch (error) {
      setError(errorMessage(error, 'Failed to update API key.'));
    }
  };

  const openEdit = (key: ApiKey) => {
    setEditKey(key);
    setFormStatus(key.status);
    // null (no override) renders as an empty field, not "null"/NaN.
    setFormRateLimit(key.rateLimit == null ? '' : String(key.rateLimit));
  };

  return (
    <div data-testid="api-keys-section">
      <h1 className="admin-page-title">API Keys Management</h1>
      <p className="admin-page-lead">One key per publisher domain, with the hourly rate limit it serves under.</p>

      {/* Create Modal */}
      {showCreate && (
        <div className="admin-modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3>Create New API Key</h3>
            <div className="admin-field">
              <label className="admin-label" htmlFor="create-key-domain">Domain</label>
              <input id="create-key-domain" className="admin-input" value={formDomain} onChange={e => setFormDomain(e.target.value)} placeholder="example.com" />
            </div>
            <div className="admin-field">
              <label className="admin-label" htmlFor="create-key-rate">Rate limit override (per hour)</label>
              <input id="create-key-rate" className="admin-input" type="number" min="1" placeholder="Leave empty to use the plan limit" value={formRateLimit} onChange={e => setFormRateLimit(e.target.value)} />
              <p className="admin-muted" style={{ marginTop: '0.35rem', fontSize: '0.8rem' }}>
                Optional. Empty means the key inherits its owner's plan
                {Object.keys(planRateLimits).length > 0 && (
                  <> ({Object.entries(planRateLimits).map(([plan, n]) => `${plan} ${n.toLocaleString()}`).join(' · ')})</>
                )}
                . Set a number only to pin this one key below or above that.
              </p>
            </div>
            <div className="admin-modal-actions">
              <button type="button" onClick={() => setShowCreate(false)} className="admin-btn-secondary">Cancel</button>
              <button type="button" onClick={handleCreate} disabled={!formDomain} className="admin-btn">Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editKey && (
        <div className="admin-modal-overlay" onClick={() => setEditKey(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3>Edit API Key: {editKey.domain}</h3>
            <div className="admin-field">
              <label className="admin-label" htmlFor="edit-key-status">Status</label>
              <select id="edit-key-status" className="admin-input" value={formStatus} onChange={e => setFormStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div className="admin-field">
              <label className="admin-label" htmlFor="edit-key-rate">Rate limit override (per hour)</label>
              <input id="edit-key-rate" className="admin-input" type="number" min="1" placeholder="Leave empty to use the plan limit" value={formRateLimit} onChange={e => setFormRateLimit(e.target.value)} />
              <p className="admin-muted" style={{ marginTop: '0.35rem', fontSize: '0.8rem' }}>
                {editKey.rateLimitSource === 'override'
                  ? `Currently pinned at ${editKey.rateLimit?.toLocaleString()}/hour. Clearing this field does not remove the pin — that needs a direct change.`
                  : `Currently inheriting ${editKey.effectiveRateLimit?.toLocaleString()}/hour from the ${editKey.ownerPlan ?? 'free'} plan.`}
              </p>
            </div>
            <div className="admin-modal-actions">
              <button type="button" onClick={() => setEditKey(null)} className="admin-btn-secondary">Cancel</button>
              <button type="button" onClick={handleEdit} className="admin-btn">Save</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="admin-muted">Loading API keys...</p>
      ) : error ? (
        <ErrorBanner message={error} onRetry={fetchApiKeys} />
      ) : (
        <div>
          <div className="admin-toolbar">
            <button
              type="button"
              data-testid="create-api-key-button"
              className="admin-btn"
              onClick={() => { setFormDomain(''); setFormRateLimit(''); setShowCreate(true); }}
            >
              Create New API Key
            </button>
          </div>

          {apiKeys.length === 0 ? (
            <EmptyState>No API keys found. Create your first API key to get started.</EmptyState>
          ) : (
            <div
              className="admin-table-region"
              role="region"
              aria-label="API keys"
              tabIndex={0}
            >
              <table data-testid="api-keys-table" className="admin-table">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>API Key</th>
                    <th>Status</th>
                    <th>Rate Limit</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((key, index) => (
                    <tr key={index}>
                      <td>{key.domain}</td>
                      <td>{key.key ? `${key.key.substring(0, 8)}...` : 'N/A'}</td>
                      <td>
                        <span className={statusBadgeClass(key.status === 'active')}>
                          {statusLabel(key.status)}
                        </span>
                      </td>
                      {/* The EFFECTIVE ceiling, always a number, with where it
                          came from. This cell used to render the raw override
                          and printed "N/A" for every key without one — which is
                          the correct, common case, shown as though something
                          were missing. */}
                      <td>
                        {key.effectiveRateLimit != null ? key.effectiveRateLimit.toLocaleString() : '—'}
                        <div className="admin-muted" style={{ fontSize: '0.75rem' }}>
                          {key.rateLimitSource === 'override'
                            ? 'pinned override'
                            : `${key.ownerPlan ?? 'free'} plan`}
                        </div>
                      </td>
                      <td>
                        <div className="cell-actions">
                          <button type="button" onClick={() => openEdit(key)} className="admin-btn-sm">Edit</button>
                          <button type="button" data-testid={`delete-api-key-${key.id}`} onClick={() => handleDelete(key)} className="admin-btn-sm is-danger">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Users Content Component
const UsersContent: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setError(null);
    try {
      const data = await apiService.getUsers();
      setUsers(data.users || []);
    } catch (error) {
      setError(errorMessage(error, 'Failed to load users.'));
    } finally {
      setLoading(false);
    }
  };

  const viewUser = async (userId: string) => {
    try {
      const user = await apiService.getUserById(userId);
      setSelectedUser(user as User);
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  };

  const setUserStatus = async (user: User, status: 'active' | 'suspended') => {
    if (status === 'suspended' &&
        !window.confirm(`Suspend ${user.email}? They will not be able to log in and their widgets will stop serving matches.`)) {
      return;
    }
    try {
      const updated = await apiService.updateUser(user.id, { status });
      setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, ...(updated as Partial<User>) } : u)));
    } catch (error) {
      setError(errorMessage(error, 'Failed to update user status.'));
    }
  };

  // Approve website: transactionally verifies AND activates a pending
  // account (distinct from Reactivate, which flips status only)
  const approveUser = async (user: User) => {
    if (!window.confirm(`Approve ${user.email} (${user.website || 'no website'})? Their account will be verified and their widget will start serving matches.`)) {
      return;
    }
    try {
      const updated = await apiService.approveUser(user.id);
      setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, ...(updated as Partial<User>) } : u)));
    } catch (error) {
      setError(errorMessage(error, 'Failed to approve user.'));
    }
  };

  return (
    <div data-testid="users-section">
      <h1 className="admin-page-title">User Management</h1>
      <p className="admin-page-lead">Publisher accounts, their plan, and the website each one serves.</p>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="admin-modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3>User Details</h3>
            <div className="admin-detail-row"><strong>Email</strong> <span>{selectedUser.email}</span></div>
            <div className="admin-detail-row"><strong>Status</strong> <span>{selectedUser.status}</span></div>
            <div className="admin-detail-row"><strong>Plan</strong> <span>{selectedUser.plan || 'free'}</span></div>
            <div className="admin-detail-row"><strong>Website</strong> <span>{selectedUser.website || 'N/A'}</span></div>
            <div className="admin-detail-row"><strong>Registered</strong> <span>{selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleString() : 'N/A'}</span></div>
            <div className="admin-detail-row"><strong>Last Login</strong> <span>{selectedUser.lastLoginAt ? new Date(selectedUser.lastLoginAt).toLocaleString() : 'Never'}</span></div>
            <div className="admin-modal-actions">
              <button type="button" onClick={() => setSelectedUser(null)} className="admin-btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="admin-muted">Loading users...</p>
      ) : error ? (
        <ErrorBanner message={error} onRetry={fetchUsers} />
      ) : (
        <div>
          {users.length === 0 ? (
            <EmptyState>No users found. Users will appear here after registration.</EmptyState>
          ) : (
            <div
              className="admin-table-region"
              role="region"
              aria-label="Publisher accounts"
              tabIndex={0}
            >
              <table data-testid="users-table" className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Plan</th>
                    <th>Website</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={index}>
                      <td>{user.email}</td>
                      <td>
                        <span className={statusBadgeClass(user.status === 'active')}>
                          {statusLabel(user.status)}
                        </span>
                      </td>
                      <td className="is-capitalized">{user.plan || 'free'}</td>
                      <td>{user.website || 'Not verified'}</td>
                      <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</td>
                      <td>
                        <div className="cell-actions">
                          <button type="button" onClick={() => viewUser(user.id)} className="admin-btn-sm">View</button>
                          {user.status === 'pending' ? (
                            <button
                              type="button"
                              data-testid={`approve-user-${user.id}`}
                              onClick={() => approveUser(user)}
                              className="admin-btn-sm is-positive"
                            >
                              Approve website
                            </button>
                          ) : user.status === 'suspended' ? (
                            <button
                              type="button"
                              data-testid={`activate-user-${user.id}`}
                              onClick={() => setUserStatus(user, 'active')}
                              className="admin-btn-sm"
                            >
                              Reactivate
                            </button>
                          ) : (
                            <button
                              type="button"
                              data-testid={`suspend-user-${user.id}`}
                              onClick={() => setUserStatus(user, 'suspended')}
                              className="admin-btn-sm is-danger"
                            >
                              Suspend
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Feeds Content Component
// The generated response types are optional-everything, so index them through
// NonNullable rather than asserting — the contract says these arrays may be
// absent and the component already renders that case.
type NetworkSignals = Awaited<ReturnType<typeof apiService.getNetworkSignals>>;
type NetworkSignalRow = NonNullable<NetworkSignals['signals']>[number];
type NetworkCoverageRow = NonNullable<NetworkSignals['coverage']>[number];

const FeedsContent: React.FC = () => {
  const [feedStats, setFeedStats] = useState<FeedStatsItem[]>([]);
  const [feedHistory, setFeedHistory] = useState<FeedHistoryItem[]>([]);
  const [signals, setSignals] = useState<NetworkSignalRow[]>([]);
  const [coverage, setCoverage] = useState<NetworkCoverageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFeedData = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      apiService.getFeedStats().then(d => setFeedStats(d.stats || [])),
      apiService.getFeedHistory().then(d => setFeedHistory(d.history || [])),
      // Failure-tolerant on its own: the merchant readings come from an
      // undocumented endpoint and their absence must not blank the feed tables
      // beside them, which is the same asymmetry the sweep itself applies.
      apiService.getNetworkSignals()
        .then(d => { setSignals(d.signals || []); setCoverage(d.coverage || []); })
        .catch(() => { setSignals([]); setCoverage([]); }),
    ]).catch(err => setError(errorMessage(err, 'Failed to load feed data.')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchFeedData();
  }, []);

  if (loading) return <p className="admin-muted">Loading feed data...</p>;

  if (error) {
    return (
      <div data-testid="feeds-section">
        <h1 className="admin-page-title">Affiliate Feeds</h1>
        <p className="admin-page-lead">Product feeds by network, and what each upload produced.</p>
        <ErrorBanner message={error} onRetry={fetchFeedData} />
      </div>
    );
  }

  return (
    <div data-testid="feeds-section">
      <h1 className="admin-page-title">Affiliate Feeds</h1>
      <p className="admin-page-lead">Product feeds by network, and what each upload produced.</p>

      <h3 className="admin-section-title">Network Statistics</h3>
      <p className="admin-muted">
        Feeds and products are the state right now. Sync runs is history — a feed synced
        nightly for a month is one feed and thirty runs.
      </p>
      {feedStats.length === 0 ? (
        <EmptyState>No feed data yet.</EmptyState>
      ) : (
        <div
          className="admin-table-region"
          role="region"
          aria-label="Affiliate network statistics"
          tabIndex={0}
        >
          <table className="admin-table">
            <thead>
              <tr>
                <th>Network</th>
                <th>Feeds</th>
                <th>Publishers</th>
                <th>Products</th>
                <th>Sync runs</th>
                <th>Avg sync</th>
                <th>Last Upload</th>
              </tr>
            </thead>
            <tbody>
              {feedStats.map((s, i) => (
                <tr key={i}>
                  <td className="is-capitalized">{s.network}</td>
                  <td>{s.feed_count}</td>
                  <td>{s.publishers}</td>
                  <td>{s.total_products}</td>
                  <td>{s.upload_runs}</td>
                  <td>{s.avg_processing_time != null ? `${s.avg_processing_time} ms` : '—'}</td>
                  <td>{s.last_upload ? new Date(s.last_upload).toLocaleDateString() : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="admin-section-title">Merchant Performance</h3>
      <p className="admin-muted">
        What each network says about the shops it lists — the newest reading per programme,
        across every account. Ordered by the same key the picker serves by, so this screen and
        what publishers actually get cannot disagree. Publishers see a percentile and no
        number: we read 2Performant's newest COMPLETED competition round while their own
        dashboard shows the running one, so a figure they could check would differ from ours.
      </p>
      {coverage.length > 0 && (
        <p className="admin-muted" data-testid="signal-coverage">
          {coverage.map(c => `${c.network}: ${c.withEpc} of ${c.programmes} carry an EPC`).join(' · ')}
        </p>
      )}
      {signals.length === 0 ? (
        <EmptyState>No merchant readings yet — the inventory sweep records these.</EmptyState>
      ) : (
        <div
          className="admin-table-region"
          role="region"
          aria-label="Merchant performance readings"
          tabIndex={0}
        >
          <table className="admin-table">
            <thead>
              <tr>
                <th>Shop</th>
                <th>Network</th>
                <th>EPC</th>
                <th>Rank</th>
                <th>Conv.</th>
                <th>Approval</th>
                <th>Commission</th>
                <th>Budget</th>
                <th>Score</th>
                <th>Confidence</th>
                <th>Measured</th>
                <th>Read</th>
              </tr>
            </thead>
            <tbody>
              {signals.map(s => (
                <tr key={`${s.network}:${s.programmeKey}`} data-testid={`signal-${s.programmeKey}`}>
                  <td title={s.reasons?.join(' ')}>{s.name || s.advertiserHost || s.programmeKey}</td>
                  <td className="is-capitalized">{s.network}</td>
                  {/* The unit is the network's own and is NOT normalised: 2Performant
                      states earnings per 100 clicks. Labelling it "per click" here
                      would be the kind of quiet unit error the column cannot carry. */}
                  <td>{s.epcValue != null ? `${s.epcValue} ${s.epcCurrency || ''}`.trim() : '—'}</td>
                  <td>{s.epcRank != null && s.epcRankOf != null ? `${s.epcRank} / ${s.epcRankOf}` : '—'}</td>
                  <td>{s.conversionRatePct != null ? `${s.conversionRatePct}%` : '—'}</td>
                  <td>{(s.approvalAmountPct ?? s.approvalCountPct) != null ? `${s.approvalAmountPct ?? s.approvalCountPct}%` : '—'}</td>
                  <td>{s.commissionMin != null ? `${s.commissionMin}%` : '—'}</td>
                  {/* funded === null is "the network did not say", never "no". */}
                  <td>{s.funded === false ? 'out of budget' : s.funded === true ? 'funded' : '—'}</td>
                  <td>{s.score != null ? s.score.toFixed(2) : '—'}</td>
                  <td>{s.confidence != null ? s.confidence.toFixed(2) : '—'}</td>
                  {/* "Measured" is the window the EPC is ABOUT; "Read" is when
                      we last asked. They came apart the day the sweep learned to
                      reuse a finished round rather than re-fetch it, and only
                      one of them says whether the number is current. */}
                  <td>{s.epcWindowEnd ? `to ${String(s.epcWindowEnd).slice(0, 10)}` : '—'}</td>
                  <td>{s.ageDays != null ? `${s.ageDays}d ago` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="admin-section-title">Processing History</h3>
      {feedHistory.length === 0 ? (
        <EmptyState>No upload history yet.</EmptyState>
      ) : (
        <div
          className="admin-table-region"
          role="region"
          aria-label="Feed processing history"
          tabIndex={0}
        >
          <table className="admin-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Network</th>
                <th>Products</th>
                <th>Success Rate</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {feedHistory.map((h, i) => (
                <tr key={i}>
                  <td>{h.filename}</td>
                  <td className="is-capitalized">{h.network}</td>
                  <td>{h.processed_products}/{h.total_products}</td>
                  <td>{h.success_rate}%</td>
                  <td>{h.created_at ? new Date(h.created_at).toLocaleDateString() : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Prospects: audit a site an operator is considering approaching, and draft an
// approach they will edit before anything leaves.
//
// Two properties this screen must keep, both from the change's own decisions:
//   - No prospect record is kept. The report lives in this component's state
//     and is gone when the tab changes; nothing about the site is written to a
//     table that names it. (The shared subject cache and call ledger are
//     written, as on every audit — so "nothing is stored" would be false.)
//   - Nothing sends from here. The draft is editable text and a compose link
//     into the operator's OWN mailbox. Our transactional domain carries
//     password resets; an outreach complaint against it would put those in
//     people's junk folders.
const ProspectsContent: React.FC = () => {
  type Result = Awaited<ReturnType<typeof apiService.auditProspect>>;
  const [url, setUrl] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Held separately from `result` so the operator's edits survive re-render and
  // are what the compose link actually carries.
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [copied, setCopied] = useState('');
  // The AI opening is a SUGGESTION the operator accepts or ignores. It is held
  // beside the draft rather than written into it, so the default wording is
  // never lost and can always be compared against.
  const [suggestion, setSuggestion] = useState<{ line: string; warnings: string[] } | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState('');
  // The second email, held ready. The first one asks a single question, so the
  // moment she answers it the operator should not be assembling this by hand.
  const [followUpSubject, setFollowUpSubject] = useState('');
  const [followUpBody, setFollowUpBody] = useState('');
  const [showFollowUp, setShowFollowUp] = useState(false);

  const run = async () => {
    if (!url.trim()) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const data = await apiService.auditProspect(url.trim());
      setResult(data);
      setSubject(data.draft?.subject || '');
      setBody(data.draft?.body || '');
      setSuggestion(null); setSuggestError('');
      setFollowUpSubject(data.followUp?.subject || '');
      setFollowUpBody(data.followUp?.body || '');
      setShowFollowUp(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Prospect audit failed');
    } finally {
      setLoading(false);
    }
  };

  const suggest = async () => {
    const r = result?.report;
    // Every field is optional on the generated type; without all three there is
    // no sentence to write, so the button does nothing rather than sending a
    // half-filled request the server would reject.
    if (!r?.host || !r.pagesRead || !r.totals?.images) return;
    setSuggesting(true); setSuggestError(''); setSuggestion(null);
    try {
      setSuggestion(await apiService.suggestProspectOpening({
        host: r.host,
        pagesRead: r.pagesRead,
        images: r.totals.images,
        bestPage: r.bestPage || undefined,
        topSubject: r.subjects?.[0]?.subject,
        topSubjectImages: r.subjects?.[0]?.images,
      }));
    } catch (e) {
      setSuggestError(e instanceof Error ? e.message : 'Could not get an alternative opening');
    } finally {
      setSuggesting(false);
    }
  };

  // Swaps the first non-empty line after the greeting. Everything else in the
  // draft — and any edit already made to it — is left alone.
  const useSuggestion = () => {
    if (!suggestion) return;
    const lines = body.split('\n');
    const i = lines.findIndex((l, idx) => idx > 0 && l.trim().length > 0);
    if (i === -1) return;
    lines[i] = suggestion.line;
    setBody(lines.join('\n'));
    setSuggestion(null);
  };

  const compose = () =>
    `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const composeFollowUp = () =>
    `mailto:?subject=${encodeURIComponent(followUpSubject)}&body=${encodeURIComponent(followUpBody)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${subject}\n\n${body}`);
      setCopied('Copied');
      setTimeout(() => setCopied(''), 2000);
    } catch {
      setCopied('Copy failed — select the text manually');
    }
  };

  const report = result?.report;

  return (
    <div data-testid="prospects-content">
      <h1 className="admin-page-title">Prospect audit</h1>
      <p className="admin-page-lead">
        Reads a few pages from the entry URL. No prospect record is kept, and nothing sends —
        the draft goes to your own mail client for you to edit first.
      </p>

      <div className="admin-inline-form">
        <input
          className="admin-input"
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') run(); }}
          placeholder="https://theirblog.com/"
          aria-label="Prospect site URL"
        />
        <button type="button" className="admin-btn" onClick={run} disabled={loading}>
          {loading ? 'Reading…' : 'Audit site'}
        </button>
      </div>

      {error && <ErrorBanner message={error} onRetry={run} />}

      {result && !result.readable && (
        <EmptyState>{result.message || 'Could not read any pages on that site.'}</EmptyState>
      )}

      {report && result?.readable && (
        <>
          <div className="admin-report">
            <p>
              <strong>{report.host}</strong>
              <span className="admin-muted">
                {' '}— read {report.pagesRead} of {report.pagesAttempted} pages,
                {' '}{report.totals?.images ?? 0} images,
                {' '}{report.totals?.missingAlt ?? 0} without alt text,
                {' '}{report.totals?.linkedToAttachmentPage ?? 0} linking to attachment pages
              </span>
            </p>
            {!!report.subjects?.length && (
              <p>
                Subjects: {report.subjects.map(s => `${s.subject} (${s.images})`).join(' · ')}
                {!!report.remainder && <span className="admin-muted"> · {report.remainder} ungrouped</span>}
              </p>
            )}
            {report.bestPage && (
              <p>
                Best page to cite:{' '}
                <a href={report.bestPage} target="_blank" rel="noreferrer noopener">{report.bestPage}</a>
              </p>
            )}
          </div>

          <div
            className="admin-table-region"
            role="region"
            aria-label="Site audit pages"
            tabIndex={0}
          >
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th>Images</th>
                  <th>No alt</th>
                  <th>Attachment links</th>
                </tr>
              </thead>
              <tbody>
                {(report.pages || []).map(p => (
                  <tr key={p.url}>
                    <td>
                      <a href={p.url} target="_blank" rel="noreferrer noopener">{p.title || p.url}</a>
                      {/* A page we could not read is shown, never dropped — otherwise
                          the totals would read as the whole site. */}
                      {!p.readable && <div className="admin-fine">{p.reason}</div>}
                    </td>
                    <td>{p.readable ? p.images : '—'}</td>
                    <td>{p.readable ? p.missingAlt : '—'}</td>
                    <td>{p.readable ? p.linkedToAttachmentPage : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="admin-section-title">Draft</h2>
          <p className="admin-page-lead">
            Read it. Change it. Then open it in your mail client and add the recipient yourself.
          </p>

          {(report?.affiliateNetworks?.length ?? 0) > 0 && (
            <p data-testid="already-monetising">
              <strong>Already running:</strong>{' '}
              {report!.affiliateNetworks!.map(a => `${a.network} (${a.links})`).join(', ')}
              {' — '}
              <span className="admin-muted">
                they have a network account and a feed already, which is what the follow-up asks for.
              </span>
            </p>
          )}

          <div className="admin-field">
            <label className="admin-label" htmlFor="draft-subject">Subject</label>
            <input
              id="draft-subject"
              className="admin-input"
              value={subject}
              onChange={e => setSubject(e.target.value)}
            />
          </div>

          <div className="admin-field">
            <label className="admin-label" htmlFor="draft-body">Message</label>
            <textarea
              id="draft-body"
              className="admin-input"
              value={body}
              onChange={e => setBody(e.target.value)}
            />
          </div>

          <div className="admin-actions-row">
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={suggest}
              disabled={suggesting || !result?.openingSuggestionAvailable}
              data-testid="suggest-opening"
            >
              {suggesting ? 'Writing…' : 'Rewrite opening with AI'}
            </button>
            {!result?.openingSuggestionAvailable && (
              <span className="admin-muted">Suggestions are switched off.</span>
            )}
            {suggestError && <span className="admin-muted">{suggestError}</span>}
          </div>

          {suggestion && (
            <div className="admin-field" data-testid="opening-suggestion">
              <label className="admin-label">Suggested opening</label>
              <p>{suggestion.line}</p>
              {suggestion.warnings.length > 0 && (
                // Shown WITH the sentence, never instead of it: the operator is
                // the reviewer, and a suggestion they cannot see is one they
                // cannot judge.
                <ul data-testid="opening-warnings">
                  {suggestion.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
              <div className="admin-actions-row">
                <button type="button" className="admin-btn-secondary" onClick={useSuggestion}>
                  Use this opening
                </button>
                <button type="button" className="admin-btn-secondary" onClick={() => setSuggestion(null)}>
                  Keep the default
                </button>
              </div>
            </div>
          )}

          <div className="admin-actions-row">
            <a className="admin-btn" href={compose()}>Open in my mail client</a>
            <button type="button" className="admin-btn-secondary" onClick={copy}>Copy</button>
            {copied && <span className="admin-muted">{copied}</span>}
          </div>

          {followUpBody && (
            <div className="admin-field">
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={() => setShowFollowUp(v => !v)}
                data-testid="toggle-follow-up"
              >
                {showFollowUp ? 'Hide the follow-up' : 'Show the follow-up (for when they say yes)'}
              </button>

              {showFollowUp && (
                <div data-testid="follow-up-draft">
                  <p className="admin-muted">
                    The first email asks one question. This is the breakdown it promises —
                    edit and send it when they answer.
                  </p>
                  <label className="admin-label" htmlFor="followup-subject">Subject</label>
                  <input
                    id="followup-subject"
                    className="admin-input"
                    value={followUpSubject}
                    onChange={e => setFollowUpSubject(e.target.value)}
                  />
                  <label className="admin-label" htmlFor="followup-body">Message</label>
                  <textarea
                    id="followup-body"
                    className="admin-input"
                    value={followUpBody}
                    onChange={e => setFollowUpBody(e.target.value)}
                  />
                  <div className="admin-actions-row">
                    <a className="admin-btn" href={composeFollowUp()}>Open follow-up in my mail client</a>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Health Content Component
// The audit tool's conversion funnel (eventspec slice 07-read-side): the
// pre-account half from audit_funnel_daily (aggregate counters, source-split),
// the post-account half from publisher_milestones for audit-born accounts.
// Answers "N people ran the audit, M converted" — the sentence that was
// unanswerable before the counters existed.
const AuditFunnelContent: React.FC = () => {
  const [data, setData] = useState<Awaited<ReturnType<typeof apiService.getAuditFunnel>> | null>(null);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  const fetchFunnel = useCallback(async (d: number) => {
    try {
      setError('');
      setData(await apiService.getAuditFunnel(d));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch audit funnel');
    }
  }, []);

  useEffect(() => { fetchFunnel(days); }, [days, fetchFunnel]);

  const hopTotal = (hop?: Record<string, number>) =>
    Object.values(hop ?? {}).reduce((a, b) => a + (b || 0), 0);

  // Display order IS the funnel order; the labels say what each hop means so
  // the numbers cannot be misread as page views or emails sent.
  const HOPS: Array<{ key: string; label: string; note: string }> = [
    { key: 'audited', label: 'Audits answered', note: 'a stranger pasted a URL and got findings' },
    { key: 'unreadable', label: 'Pages we could not read', note: 'bot challenge / empty — said so, never "you’re fine"' },
    { key: 'report_stored_new', label: 'Reports → new account', note: 'email traded; pending account + magic link' },
    { key: 'report_stored_existing', label: 'Reports → existing account', note: 'attached; no link (enumeration rules)' },
    { key: 'link_redeemed', label: 'Magic links redeemed', note: 'total only — a link carries no source' },
  ];

  const CONTINUATION: Array<{ key: string; label: string }> = [
    { key: 'accountsCreated', label: 'Audit-born accounts created' },
    { key: 'siteVerified', label: 'Site verified' },
    { key: 'accountApproved', label: 'Account approved' },
    { key: 'apiKeyIssued', label: 'API key issued' },
    { key: 'feedConnected', label: 'Feed connected' },
    { key: 'widgetFirstRequest', label: 'Widget calling' },
    { key: 'firstClick', label: 'First click' },
  ];

  return (
    <div data-testid="audit-funnel-section">
      <h1 className="admin-page-title">Audit funnel</h1>
      <p className="admin-page-lead">
        How the public audit tool converts, per acquisition source. Aggregate counters only — no
        URLs, no visitors. Audit-born accounts mint no signed-up milestone by design; their
        journey continues below from site verification.
      </p>

      <WindowPicker days={days} options={[7, 30, 90]} onChange={setDays} />

      {error ? (
        <ErrorBanner message={error} onRetry={() => fetchFunnel(days)} />
      ) : !data ? (
        <p className="admin-muted">Loading…</p>
      ) : (
        <>
          <div className="admin-panel">
            <h3>{`Funnel (${data.days}d)`}</h3>
            <table className="admin-mini-table">
              <thead>
                <tr>
                  <th>Hop</th><th className="num">Total</th>
                  <th className="num">outreach</th><th className="num">home</th>
                  <th className="num">organic</th><th className="num">other</th>
                </tr>
              </thead>
              <tbody>
                {HOPS.map(h => {
                  const hop = (data.hops as any)?.[h.key] ?? {};
                  return (
                    <tr key={h.key}>
                      <td className="name">{h.label}<div className="sub">{h.note}</div></td>
                      <td className="num">{hopTotal(hop)}</td>
                      <td className="num">{hop.outreach ?? 0}</td>
                      <td className="num">{hop.home ?? 0}</td>
                      <td className="num">{hop.organic ?? 0}</td>
                      <td className="num">{hop.other ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="admin-panel">
            <h3>After the account (audit-born publishers, same window)</h3>
            <table className="admin-mini-table">
              <tbody>
                {CONTINUATION.map(c => (
                  <tr key={String(c.key)}>
                    <td className="name">{c.label}</td>
                    <td className="num">{(data.continuation as any)?.[c.key] ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// Acquisition: where signups and leads came from (growth-plan Phase 0
// reporting over the signup_attribution / leads capture).
const AcquisitionContent: React.FC = () => {
  const [data, setData] = useState<Awaited<ReturnType<typeof apiService.getAcquisition>> | null>(null);
  const [error, setError] = useState('');
  const [days, setDays] = useState(90);

  const fetchAcquisition = useCallback(async (d: number) => {
    try {
      setError('');
      setData(await apiService.getAcquisition(d));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch acquisition');
    }
  }, []);

  useEffect(() => { fetchAcquisition(days); }, [days, fetchAcquisition]);

  const attrText = (attribution?: Record<string, string> | null) => {
    if (!attribution) return '—';
    return ['utm_source', 'utm_medium', 'utm_campaign', 'referrer']
      .filter(k => attribution[k])
      .map(k => `${k.replace('utm_', '')}: ${attribution[k]}`)
      .join(' · ') || '—';
  };

  const SourceTable: React.FC<{ title: string; rows: Array<{ source?: string; medium?: string; count?: number }> }> = ({ title, rows }) => (
    <div className="admin-panel">
      <h3>{title}</h3>
      {rows.length === 0 ? (
        <p className="admin-muted">Nothing in this window yet.</p>
      ) : (
        <table className="admin-mini-table">
          <thead>
            <tr>
              <th>Source</th><th>Medium</th><th className="num">Count</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="name">{r.source}</td>
                <td className="sub">{r.medium || '—'}</td>
                <td className="num">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div data-testid="acquisition-section">
      <h1 className="admin-page-title">Acquisition</h1>
      <p className="admin-page-lead">Where signups and leads came from, over the window you pick.</p>

      <WindowPicker days={days} options={[30, 90, 365]} onChange={setDays} />

      {error ? (
        <ErrorBanner message={error} onRetry={() => fetchAcquisition(days)} />
      ) : !data ? (
        <p className="admin-muted">Loading…</p>
      ) : (
        <>
          <div className="admin-panel-grid">
            <SourceTable title={`Signups by source (${data.days}d)`} rows={data.signupsBySource ?? []} />
            <SourceTable title={`Leads by source (${data.days}d)`} rows={data.leadsBySource ?? []} />
          </div>
          <div className="admin-panel-grid">
            <div className="admin-panel">
              <h3>Recent signups</h3>
              {(data.recentSignups ?? []).map((u, i) => (
                <div key={i} className="record-row">
                  <div className="record-title">{u.email}</div>
                  <div className="record-meta">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : ''} · {attrText(u.attribution as any)}
                  </div>
                </div>
              ))}
              {(data.recentSignups ?? []).length === 0 && <p className="admin-muted">None yet.</p>}
            </div>
            <div className="admin-panel">
              <h3>Recent leads</h3>
              {(data.recentLeads ?? []).map((l, i) => (
                <div key={i} className="record-row">
                  <div className="record-title">{l.email}</div>
                  <div className="record-meta">
                    {l.created_at ? new Date(l.created_at).toLocaleDateString() : ''} · {l.source || ''} · {attrText(l.attribution as any)}
                  </div>
                </div>
              ))}
              {(data.recentLeads ?? []).length === 0 && <p className="admin-muted">None yet.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Read-only listing of the feature flag registry (services/feature-flags.ts
// on the API side). No edit UI here on purpose — a flag's state changes by a
// reviewed code diff and a deploy, not a click, so this view exists only to
// answer "what's gated right now, and for whom" without reading source.
const FlagsContent: React.FC = () => {
  const [data, setData] = useState<Awaited<ReturnType<typeof apiService.getFlags>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFlags = useCallback(async () => {
    setError(null);
    try {
      setData(await apiService.getFlags());
    } catch (e) {
      setData(null);
      setError(errorMessage(e, 'Failed to load feature flags.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  const flags = data?.flags ?? [];

  const stateBadgeClass = (state?: string): string => {
    switch (state) {
      case 'on': return 'status-badge is-active';
      case 'beta': return 'status-badge is-pending';
      default: return 'status-badge is-inactive';
    }
  };

  return (
    <div data-testid="flags-section">
      <h1 className="admin-page-title">Feature Flags</h1>
      <p className="admin-page-lead">
        Every declared gate, read straight from the registry. Off → beta (named accounts)
        → on (everyone) → the entry is deleted along with its call sites.
      </p>

      {loading ? (
        <p className="admin-muted">Loading feature flags...</p>
      ) : error ? (
        <ErrorBanner message={error} onRetry={fetchFlags} />
      ) : flags.length === 0 ? (
        <EmptyState>No flags declared.</EmptyState>
      ) : (
        <div className="admin-table-region" role="region" aria-label="Feature flags" tabIndex={0}>
          <table data-testid="flags-table" className="admin-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>State</th>
                <th>Accounts</th>
                <th>Age</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((flag) => (
                <tr key={flag.key}>
                  <td><code>{flag.key}</code></td>
                  <td><span className={stateBadgeClass(flag.state)}>{flag.state}</span></td>
                  <td>
                    {flag.state === 'beta' && flag.beta?.length ? `beta: ${flag.beta.join(', ')}` : null}
                    {flag.state === 'on' && flag.except?.length ? `except: ${flag.except.join(', ')}` : null}
                    {flag.state === 'on' && !flag.except?.length ? 'everyone' : null}
                    {flag.state === 'off' ? '—' : null}
                  </td>
                  <td>{flag.ageDays}d</td>
                  <td className="admin-muted">{flag.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const HealthContent: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchHealthData();
  }, []);

  const fetchHealthData = async () => {
    setError(null);
    try {
      const data = await apiService.getSystemHealth();
      setHealth(data);
    } catch (error) {
      setHealth(null);
      setError(errorMessage(error, 'Failed to load system health.'));
    } finally {
      setLoading(false);
    }
  };

  const serviceStatus = (name: string): string =>
    health?.services.find(s => s.name.toLowerCase().includes(name))?.status || 'unknown';

  return (
    <div data-testid="health-section">
      <h1 className="admin-page-title">System Health</h1>
      <p className="admin-page-lead">Live status for each service behind the widget.</p>

      {loading ? (
        <p className="admin-muted">Loading system health...</p>
      ) : error ? (
        <ErrorBanner message={error} onRetry={fetchHealthData} />
      ) : (
        <>
          <div className="health-grid">
            <HealthCard title="API Service" status={serviceStatus('api')} />
            <HealthCard title="Database" status={serviceStatus('database')} />
            <HealthCard title="Redis Cache" status={serviceStatus('redis')} />
            <HealthCard title="Overall Status" status={health?.overall || 'unknown'} />
          </div>

          <div>
            <h3 className="admin-section-title">System Metrics</h3>
            <div className="admin-panel admin-metrics">
              <p><strong>Uptime:</strong> {health?.metrics?.uptime ?? 'N/A'}</p>
              <p><strong>Response Time:</strong> {health?.metrics?.responseTime != null ? `${health.metrics.responseTime}ms average` : 'N/A'}</p>
              <p><strong>Last Check:</strong> {health?.metrics?.lastCheck ? new Date(health.metrics.lastCheck).toLocaleString() : 'N/A'}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// The disposition counters, read as two questions: did readers get an overlay
// without waiting, and are the background fills finishing.
//
// Every ratio here is computed from a numerator and a denominator this file
// names explicitly. The endpoint deliberately ships raw counts only, so nobody
// inherits a rounding decision made server-side and nobody can silently change
// what a percentage means.
const MatchingContent: React.FC = () => {
  const [data, setData] = useState<Awaited<ReturnType<typeof apiService.getMatchOps>> | null>(null);
  const [error, setError] = useState('');
  const [days, setDays] = useState(14);

  const fetchOps = useCallback(async (d: number) => {
    try {
      setError('');
      setData(await apiService.getMatchOps(d));
    } catch (e) {
      setError(errorMessage(e, 'Failed to fetch matching counters'));
    }
  }, []);

  useEffect(() => { fetchOps(days); }, [days, fetchOps]);

  const series = data?.series ?? [];
  const since = (data?.since ?? {}) as Record<string, string | null>;

  // day -> metric -> count. Built once; every block below reads it.
  const byDay = new Map<string, Record<string, number>>();
  for (const point of series) {
    const row = byDay.get(point.day!) ?? {};
    row[point.metric!] = point.count ?? 0;
    byDay.set(point.day!, row);
  }
  const dayList = Array.from(byDay.keys()).sort();

  // A metric that did not exist on a given day is UNKNOWN, not zero. Rendering
  // it as 0 would assert we observed none of something nothing was watching for.
  const valueOn = (day: string, metric: string): number | null => {
    const start = since[metric];
    if (start && day < start) return null;
    return byDay.get(day)?.[metric] ?? 0;
  };

  // A metric's FIRST day is partial: it started being recorded partway through,
  // so that day's total mixes hours that were counted with hours that were not.
  // The raw counts are still shown — they are real — but nothing DERIVED from
  // them is computed for that day.
  //
  // Seen immediately on rollout: 201 of the day's 203 fills were queued before
  // the terminal counters existed, so the residual read "201 running or lost".
  // A dashboard built to stop a false alarm would have opened with one.
  //
  // This cannot be papered over by shifting the declared `since` forward — the
  // route takes the EARLIER of the declared date and the first day rows exist,
  // so real data always wins. Which is correct; the fix belongs here, in what
  // the day is allowed to claim.
  const isPartial = (day: string, metrics: string[]): boolean =>
    metrics.some(m => since[m] === day);

  // THE rule, in one place: a derived number needs every input known AND a day
  // that was fully counted. It was previously spelled four different ways
  // across this component, and the partial-day half was missing from one of
  // them — which is how the rollout day nearly shipped "201 running or lost".
  // A fifth derived column now gets both halves by construction.
  const derivedSum = (day: string, metrics: string[]): number | null =>
    isPartial(day, metrics) ? null : sumOver(metrics, day);
  const sumOver = (metrics: string[], day: string): number | null => {
    let total = 0;
    for (const m of metrics) {
      const v = valueOn(day, m);
      if (v === null) return null;
      total += v;
    }
    return total;
  };

  const SERVE_MIX = ['serve_memory', 'serve_fresh', 'serve_stale', 'serve_miss'];
  const WITHOUT_WAITING = ['serve_memory', 'serve_fresh', 'serve_stale'];
  const TERMINALS = ['fill_completed', 'fill_blocked', 'fill_skipped_fresh', 'fill_failed'];

  const total = (metric: string, days: string[] = dayList) =>
    days.reduce((n, d) => n + (valueOn(d, metric) ?? 0), 0);

  // The headline ratios are computed ONLY over days where the whole serve mix
  // was being recorded. Summing across a metric's start date would fold days
  // with no memory counts into the denominator as zeros, which understates the
  // ratio by exactly the amount the missing metric would have improved it —
  // the same inverted signal the counter fix removed, reintroduced in the view.
  // The per-day table refuses to compute that ratio; the card must not either.
  const comparableDays = dayList.filter(d => derivedSum(d, SERVE_MIX) !== null);

  const servedWithoutWaiting = WITHOUT_WAITING.reduce(
    (n, m) => n + total(m, comparableDays), 0
  );
  const allServes = servedWithoutWaiting + total('serve_miss', comparableDays);
  const pct = (num: number, den: number) => (den === 0 ? '—' : `${Math.round((num / den) * 100)}%`);

  const cell = (v: number | null) => (v === null ? <span className="admin-muted" title="Not recorded yet on this day">—</span> : v);

  return (
    <div data-testid="matching-section">
      <h1 className="admin-page-title">Matching</h1>
      <p className="admin-page-lead">
        Whether readers are getting overlays without waiting, and whether the background fills
        behind them are finishing.
      </p>

      <WindowPicker days={days} options={[7, 14, 30]} onChange={setDays} />

      {error ? (
        <ErrorBanner message={error} onRetry={() => fetchOps(days)} />
      ) : !data ? (
        <p className="admin-muted">Loading…</p>
      ) : dayList.length === 0 ? (
        // A quiet window and a broken counter look identical if this says 0%.
        <EmptyState>No matching activity recorded in this window.</EmptyState>
      ) : (
        <>
          <div className="admin-panel-grid">
            <StatCard title="Served without waiting" value={pct(servedWithoutWaiting, allServes)} />
            <StatCard title="Served from memory" value={pct(total('serve_memory', comparableDays), allServes)} />
            <StatCard title="Cold misses" value={total('serve_miss')} />
            <StatCard title="Fills queued" value={total('fill_queued')} />
          </div>
          {comparableDays.length < dayList.length && (
            <p className="admin-muted" data-testid="partial-window-note">
              The two percentages above cover {comparableDays.length} of {dayList.length} days.
              The rest predate one of the counters they need, or are the partial day a counter
              started on, so a figure for them would be a guess rather than a measurement.
            </p>
          )}

          <div className="admin-panel">
            <h3>Serve mix</h3>
            <p className="admin-muted">
              Every request lands in exactly one of these. &ldquo;Memory&rdquo; never touched
              Postgres; &ldquo;miss&rdquo; is the only one where the reader saw no overlay.
            </p>
            <table className="admin-mini-table" data-testid="serve-mix-table">
              <thead>
                <tr>
                  <th>Day</th><th className="num">Memory</th><th className="num">Fresh</th>
                  <th className="num">Stale</th><th className="num">Miss</th>
                  <th className="num">Without waiting</th>
                </tr>
              </thead>
              <tbody>
                {dayList.map(d => {
                  const all = derivedSum(d, SERVE_MIX);
                  const ok = derivedSum(d, WITHOUT_WAITING);
                  return (
                    <tr key={d}>
                      <td className="name">{d}</td>
                      <td className="num">{cell(valueOn(d, 'serve_memory'))}</td>
                      <td className="num">{cell(valueOn(d, 'serve_fresh'))}</td>
                      <td className="num">{cell(valueOn(d, 'serve_stale'))}</td>
                      <td className="num">{cell(valueOn(d, 'serve_miss'))}</td>
                      <td className="num">{ok === null || all === null ? '—' : pct(ok, all)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="admin-panel">
            <h3>Fill accounting</h3>
            <p className="admin-muted">
              Queued must equal the four outcomes added together. &ldquo;Skipped&rdquo; is a
              success — the entry was already fresh, so a product search was saved. Anything left
              over is a fill still running, or the few seconds a restart drops.
            </p>
            <table className="admin-mini-table" data-testid="fill-accounting-table">
              <thead>
                <tr>
                  <th>Day</th><th className="num">Queued</th><th className="num">Completed</th>
                  <th className="num">Blocked</th><th className="num">Skipped</th>
                  <th className="num">Failed</th><th className="num">Running or lost</th>
                </tr>
              </thead>
              <tbody>
                {dayList.map(d => {
                  const queued = valueOn(d, 'fill_queued');
                  const done = derivedSum(d, TERMINALS);
                  const residual = queued === null || done === null ? null : queued - done;
                  return (
                    <tr key={d}>
                      <td className="name">{d}</td>
                      <td className="num">{cell(queued)}</td>
                      <td className="num">{cell(valueOn(d, 'fill_completed'))}</td>
                      <td className="num">{cell(valueOn(d, 'fill_blocked'))}</td>
                      <td className="num">{cell(valueOn(d, 'fill_skipped_fresh'))}</td>
                      <td className="num">{cell(valueOn(d, 'fill_failed'))}</td>
                      <td className="num">{cell(residual)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="admin-panel">
            <h3>Legacy cache keys</h3>
            <p className="admin-muted">
              A subset of the stale column above, not an extra serve. It has to reach zero before
              the pre-042 key fallback can be removed.
            </p>
            <p className="admin-metrics">
              <strong>{total('serve_legacy')}</strong> legacy-key hits in this window.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

// Helper Components
const StatCard: React.FC<{ title: string; value: any }> = ({ title, value }) => (
  <div className="stat-card">
    <h3 className="stat-label">{title}</h3>
    <p className="stat-number">{value || 0}</p>
  </div>
);

const HealthCard: React.FC<{ title: string; status: string }> = ({ title, status }) => {
  // The tri-state colour class the E2E suite reads on the indicator.
  const statusClass = status === 'healthy' || status === 'unhealthy' ? status : 'unknown';
  // The dot keeps the finer distinction: "not_configured" is a deliberate
  // absence, so it reads neutral grey rather than a warning amber.
  const dotClass = status === 'not_configured' ? 'not_configured' : statusClass;

  return (
    <div className={`health-card is-${dotClass}`}>
      <span className="health-dot" aria-hidden="true"></span>
      <div>
        <h3>{title}</h3>
        <p className={`status-indicator status-text ${statusClass}`}>
          {status.replace(/_/g, ' ')}
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Outreach: the review queue the whole pipeline waits on.
//
// `eligibleProspects` requires `contact_verified`, so everything discovery
// finds is inert until a human ticks the address. Until this screen existed the
// only way to tick one was a REPL, which is why eight prospects sat unverified.
//
// WHAT THIS SCREEN CANNOT DO, and the absences are deliberate:
//   - it cannot send, approve a batch, or start a discovery run
//   - it cannot un-suppress: reversing an opt-out needs an authorization story
//     this surface does not have, so the route does not exist to call
// See routes/admin-outreach.ts for the reasoning; a test keeps those routes 404.
// ---------------------------------------------------------------------------
type OutreachSummary = Awaited<ReturnType<typeof apiService.getOutreachSummary>>;
type OutreachProspect = NonNullable<Awaited<ReturnType<typeof apiService.getOutreachProspects>>['prospects']>[number];
type OutreachCandidate = NonNullable<Awaited<ReturnType<typeof apiService.getOutreachCandidates>>['candidates']>[number];
type FormPacket = NonNullable<Awaited<ReturnType<typeof apiService.prepareFormPackets>>['packets']>[number];

// The contact-form channel (openspec 4b).
//
// Measured: 5 of the 8 Stage-A prospects publish no address anywhere. This is
// how they are reached, and it lives in the UI rather than on the CLI because
// the actual job is COPY TEXT AND PASTE IT INTO A BROWSER FORM. A terminal is
// the worst place to do that — the first cut put it there and was wrong.
//
// Nothing on this screen submits a form. The operator is the transport.
const FormChannelSection: React.FC = () => {
  const [cohort, setCohort] = useState<FormPacket[]>([]);
  const [skipped, setSkipped] = useState<Array<{ domain: string; reason: string }>>([]);
  const [reviewed, setReviewed] = useState<Set<string>>(new Set());
  const [approvalId, setApprovalId] = useState<number | null>(null);
  const [packets, setPackets] = useState<FormPacket[]>([]);
  const [submitted, setSubmitted] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState<string | null>(null);
  const [phase, setPhase] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stage = 'A';

  const preview = async () => {
    setError(null);
    // Measuring is SLOW — a bounded three-page audit per prospect, politely
    // paced. Saying so beats a spinner that looks hung.
    setPhase('Measuring each site (3 pages each, robots.txt honoured)…');
    try {
      const res = await apiService.previewFormCohort({ stage });
      setCohort(res.items || []);
      setSkipped(res.skipped || []);
      setReviewed(new Set());
      setApprovalId(null);
      setPackets([]);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPhase(null);
    }
  };

  const approve = async () => {
    setError(null);
    setPhase('Recording the approval…');
    try {
      const res = await apiService.approveFormCohort(Array.from(reviewed), { stage });
      setApprovalId(res.approvalId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPhase(null);
    }
  };

  const prepare = async () => {
    if (!approvalId) return;
    setError(null);
    setPhase('Preparing packets…');
    try {
      const res = await apiService.prepareFormPackets(approvalId, { stage });
      setPackets(res.packets || []);
      if (res.skipped?.length) setSkipped(res.skipped);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPhase(null);
    }
  };

  const markSubmitted = async (domain: string) => {
    setError(null);
    setPhase(`Recording ${domain}…`);
    try {
      await apiService.confirmFormSubmission(domain, stage);
      setSubmitted(new Set(Array.from(submitted).concat(domain)));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPhase(null);
    }
  };

  const copy = async (packet: FormPacket) => {
    await navigator.clipboard?.writeText(packet.text);
    setCopied(packet.domain);
    setTimeout(() => setCopied(null), 2000);
  };

  // Stage A requires every message read, and the checkbox IS that claim — so it
  // is the operator ticking each one, not a "select all".
  const allReviewed = cohort.length > 0 && reviewed.size === cohort.length;

  return (
    <div data-testid="outreach-form-channel">
      <h2 className="admin-section-title">Contact-form outreach</h2>
      <p className="muted-note">
        For publishers who list no address. Nothing here submits anything — you paste
        each message into their form yourself.
      </p>

      {error && <div className="admin-error" role="alert">{error}</div>}
      {phase && <div className="muted-note" role="status">{phase}</div>}

      {!cohort.length && !packets.length && (
        <button className="admin-btn-secondary" onClick={preview} disabled={!!phase}>
          Show the cohort
        </button>
      )}

      {cohort.length > 0 && !packets.length && (
        <>
          <table className="admin-table" data-testid="form-cohort">
            <thead>
              <tr><th>Read</th><th>Domain</th><th>Message</th></tr>
            </thead>
            <tbody>
              {cohort.map(c => (
                <tr key={c.domain}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`I read the message for ${c.domain}`}
                      checked={reviewed.has(c.domain)}
                      onChange={e => {
                        const next = new Set(reviewed);
                        if (e.target.checked) next.add(c.domain); else next.delete(c.domain);
                        setReviewed(next);
                      }}
                    />
                  </td>
                  <td>{c.domain}</td>
                  <td><pre className="outreach-message">{c.text}</pre></td>
                </tr>
              ))}
            </tbody>
          </table>

          {!approvalId ? (
            <button
              className="admin-btn"
              disabled={!allReviewed || !!phase}
              onClick={approve}
            >
              {allReviewed
                ? `Approve ${cohort.length} message(s)`
                : `Read all ${cohort.length} to approve (${reviewed.size} read)`}
            </button>
          ) : (
            <button className="admin-btn" onClick={prepare} disabled={!!phase}>
              Prepare packets
            </button>
          )}
        </>
      )}

      {packets.map(p => (
        <div key={p.domain} className="admin-card" data-testid={`packet-${p.domain}`}>
          <h3>{p.domain}</h3>
          <pre className="outreach-message">{p.text}</pre>
          <div className="admin-row-actions">
            <a className="admin-btn-sm" href={p.formUrl}
               target="_blank" rel="noopener noreferrer">Open their form ↗</a>
            <button className="admin-btn-sm" onClick={() => copy(p)}>
              {copied === p.domain ? 'Copied' : 'Copy message'}
            </button>
            {submitted.has(p.domain) ? (
              <span className="muted-note">recorded as sent</span>
            ) : (
              <button className="admin-btn-sm is-positive" disabled={!!phase}
                      onClick={() => markSubmitted(p.domain)}>
                I submitted this
              </button>
            )}
          </div>
        </div>
      ))}

      {skipped.length > 0 && (
        <div className="muted-note" data-testid="form-skipped">
          {skipped.map(s => <div key={s.domain}>{s.domain}: {s.reason}</div>)}
        </div>
      )}
    </div>
  );
};

const OutreachContent: React.FC = () => {
  const [summary, setSummary] = useState<OutreachSummary | null>(null);
  const [prospects, setProspects] = useState<OutreachProspect[]>([]);
  const [candidates, setCandidates] = useState<OutreachCandidate[]>([]);
  const [showVerified, setShowVerified] = useState(false);
  const [showLedger, setShowLedger] = useState(false);
  const [edited, setEdited] = useState<Record<string, string>>({});
  // Form-channel prospects are confirmed on their contact URL, not an address,
  // so they need their own edit map — reusing `edited` would send a URL to the
  // email endpoint the moment someone typed in the wrong row.
  const [editedForm, setEditedForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        apiService.getOutreachSummary(),
        apiService.getOutreachProspects({ verified: showVerified ? 'true' : 'false' }),
      ]);
      setSummary(s);
      setProspects(p.prospects || []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load outreach');
    }
  }, [showVerified]);

  useEffect(() => { load(); }, [load]);

  const loadLedger = async () => {
    setShowLedger(true);
    try {
      const c = await apiService.getOutreachCandidates({ limit: 100 });
      setCandidates(c.candidates || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the ledger');
    }
  };

  const confirm = async (p: OutreachProspect) => {
    const address = (edited[p.domain] ?? p.contactAddress ?? '').trim();
    if (!address) return;
    setBusy(p.domain);
    setError(null);
    try {
      await apiService.verifyOutreachContact(p.domain, address);
      setNote(`${p.domain} confirmed — it can now be drafted.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not confirm that address');
    } finally {
      setBusy(null);
    }
  };

  // The counterpart to `confirm` for the form channel. verifyFormContact, its
  // route and this client wrapper all existed and shipped; nothing ever called
  // them, so a prospect with a contact form but no address could never be
  // confirmed — and eligibleProspects requires contact_verified for BOTH
  // channels, so the entire contact-form pipeline was unreachable from here.
  const confirmForm = async (p: OutreachProspect) => {
    const formUrl = (editedForm[p.domain] ?? p.contactUrl ?? '').trim();
    if (!formUrl) return;
    setBusy(p.domain);
    setError(null);
    try {
      await apiService.verifyOutreachFormUrl(p.domain, formUrl);
      setNote(`${p.domain} confirmed as a contact form — it can now be drafted.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not confirm that form URL');
    } finally {
      setBusy(null);
    }
  };

  const neverContact = async (p: OutreachProspect) => {
    // Irreversible from this screen, so it asks first.
    if (!window.confirm(`Never contact ${p.domain}? This cannot be undone here.`)) return;
    setBusy(p.domain);
    setError(null);
    try {
      await apiService.suppressOutreachDomain(p.domain);
      setNote(`${p.domain} added to the do-not-contact list.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not suppress that domain');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="outreach-content">
      <h1 className="admin-page-title">Outreach</h1>
      <p className="admin-page-subtitle">
        Everything discovery finds arrives unverified. A prospect cannot be drafted or sent
        until someone confirms the address below — that tick is the only step no machine performs.
      </p>

      {summary && (
        <div className="stats-grid" data-testid="outreach-summary">
          <StatCard title="Awaiting your tick" value={String(summary.prospects?.awaiting ?? 0)} />
          <StatCard title="Confirmed" value={String(summary.prospects?.verified ?? 0)} />
          <StatCard title="No address found" value={String(summary.prospects?.no_address ?? 0)} />
          <StatCard title="Do not contact" value={String(summary.suppressed ?? 0)} />
          <StatCard title="Actually contacted" value={String(summary.contacted ?? 0)} />
        </div>
      )}

      {error && <div className="error-message" role="alert">{error}</div>}
      {note && <div className="success-message" role="status">{note}</div>}

      <div className="admin-toolbar">
        <button
          className={showVerified ? 'admin-btn-secondary' : 'admin-btn'}
          onClick={() => setShowVerified(false)}
        >
          Awaiting a tick
        </button>
        <button
          className={showVerified ? 'admin-btn' : 'admin-btn-secondary'}
          onClick={() => setShowVerified(true)}
        >
          Already confirmed
        </button>
      </div>

      {prospects.length === 0 ? (
        <p className="empty-state">
          {showVerified
            ? 'Nothing confirmed yet.'
            : 'Nothing waiting. Run `npm run outreach:discover` to find more.'}
        </p>
      ) : (
        <table className="admin-table" data-testid="outreach-prospects">
          <thead>
            <tr>
              <th>Publisher</th>
              <th>Address</th>
              <th>Why we found them</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {prospects.map(p => (
              <tr key={p.domain}>
                <td>
                  <strong>{p.domain}</strong>
                  {p.networks && p.networks.length > 0 && (
                    <div className="muted-note">runs {p.networks.join(', ')}</div>
                  )}
                  {p.contactUrl && (
                    <div className="muted-note">
                      <a href={p.contactUrl} target="_blank" rel="noopener noreferrer">contact page</a>
                    </div>
                  )}
                </td>
                <td>
                  {p.contactVerified ? (
                    <>
                      <div>{p.contactAddress}</div>
                      <div className="muted-note">confirmed by {p.contactVerifiedBy}</div>
                    </>
                  ) : p.contactAddress ? (
                    <input
                      type="email"
                      className="admin-input"
                      aria-label={`Contact address for ${p.domain}`}
                      value={edited[p.domain] ?? p.contactAddress}
                      onChange={e => setEdited({ ...edited, [p.domain]: e.target.value })}
                    />
                  ) : (
                    // No address is the normal outcome for a contact-form-only
                    // site, not an error — and it is confirmable on its own
                    // terms. What a human ticks here is not "this address is
                    // theirs" but "this URL is really their contact form"; the
                    // server refuses any URL that is not https and on the
                    // prospect's own host.
                    <>
                      <input
                        type="url"
                        className="admin-input"
                        aria-label={`Contact form URL for ${p.domain}`}
                        placeholder={`https://${p.domain}/contact`}
                        value={editedForm[p.domain] ?? p.contactUrl ?? ''}
                        onChange={e => setEditedForm({ ...editedForm, [p.domain]: e.target.value })}
                      />
                      <div className="muted-note">no address — confirm their contact form instead</div>
                    </>
                  )}
                </td>
                <td className="muted-note">{p.discoverySource}</td>
                <td>
                  {!p.contactVerified && (
                    <div className="admin-row-actions">
                      {/* One tick per channel. Which one a row gets is decided
                          by whether an address was found, the same fact that
                          decides which column above is editable. */}
                      {(edited[p.domain] ?? p.contactAddress) ? (
                        <button
                          className="admin-btn-sm is-positive"
                          disabled={busy === p.domain}
                          onClick={() => confirm(p)}
                        >
                          {busy === p.domain ? 'Confirming…' : 'Confirm'}
                        </button>
                      ) : (
                        <button
                          className="admin-btn-sm is-positive"
                          disabled={busy === p.domain || !(editedForm[p.domain] ?? p.contactUrl)}
                          onClick={() => confirmForm(p)}
                        >
                          {busy === p.domain ? 'Confirming…' : 'Confirm form'}
                        </button>
                      )}
                      <button
                        className="admin-btn-sm is-danger"
                        disabled={busy === p.domain}
                        onClick={() => neverContact(p)}
                      >
                        Never contact
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <FormChannelSection />

      <h2 className="admin-section-title">What discovery decided</h2>
      {!showLedger ? (
        <button className="admin-btn-secondary" onClick={loadLedger}>Show the ledger</button>
      ) : (
        <table className="admin-table" data-testid="outreach-candidates">
          <thead>
            <tr><th>Domain</th><th>Verdict</th><th>Reason</th><th>Seen</th></tr>
          </thead>
          <tbody>
            {candidates.map(c => (
              <tr key={c.domain}>
                <td>{c.domain}</td>
                <td><span className={`badge badge-${c.verdict}`}>{c.verdict}</span></td>
                <td className="muted-note">{c.reason}</td>
                <td>{c.timesSeen}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Dashboard;
